import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { DropdownMenuItem } from "@volca/ui/components/ui/dropdown-menu";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { toast } from "@volca/ui/lib/toast";
import { useApi } from "@/core/api";
import { dayjs } from "@/core/utils/date";
import { useCanManageProfitability } from "@/profitability/access";
import { invalidateReportViews, reportKey } from "@/profitability/query";
import { reportError } from "@/profitability/utils";

export interface RunReview {
  run_id: string;
  reviewed_at: string;
  reviewed_by: { id: string; full_name: string } | null;
}

export interface PeriodResult {
  run_id: string;
  created_at: string;
  completed_at: string | null;
  reviewed_at: string | null;
  chosen_at: string | null;
  chosen_by: { id: string; full_name: string } | null;
}

export interface ReviewState {
  review: RunReview | null;
  /** A newer run of a period someone had already reviewed. */
  revised_after_review?: boolean;
  reviewed_run_id?: string | null;
  /** The run that is the period's result: its newest completed run. */
  standing?: PeriodResult | null;
}

const path = (businessId: string, runId: string) =>
  `/businesses/${businessId}/profitability_report_runs/${runId}/review`;

/** Whether someone signed off on this period's report, and the way to. */
export const usePeriodReview = (businessId: string, runId: string) => {
  const api = useApi();
  const client = useQueryClient();
  const key = [...reportKey(businessId), "run-review", runId];
  const state = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      api
        .get<ReviewState>(path(businessId, runId), { signal })
        .then((res) => res.data),
    enabled: !!runId,
    retry: false,
  });
  const mark = useMutation({
    mutationFn: (reviewed: boolean) =>
      (reviewed
        ? api.post<ReviewState>(path(businessId, runId))
        : api.delete<ReviewState>(path(businessId, runId))
      ).then((res) => res.data),
    // A review can change which run is the period's result, which the
    // template's pages read, so they reload too.
    onSuccess: (data) => {
      client.setQueryData(key, data);
      void invalidateReportViews(client, businessId);
    },
    onError: (error) =>
      toast.error("The review could not be saved", {
        description: reportError(error),
      }),
  });
  return { state, mark };
};

/**
 * Takes the period a run stands for off its template, with every rerun of it.
 * Every page of the template reads its periods, so they all reload.
 */
export const useRemovePeriod = (businessId: string) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api
        .delete<{
          removed: number;
          /** Every run of the period, which no link may name any longer. */
          run_ids: string[];
        }>(
          `/businesses/${businessId}/profitability_report_runs/${runId}/period`,
        )
        .then((res) => res.data),
    // Not awaited: the reload drops the removed period's row, and the caller's
    // own onSuccess (the toast, leaving the period) runs only while it is mounted.
    onSuccess: () => {
      void invalidateReportViews(client, businessId);
    },
  });
};

/**
 * The period's sign-off as a badge beside its name: reviewed (green), or
 * revised after it was (amber), so a correction never quietly replaces a
 * result someone stood behind.
 */
export const ReviewBadge = ({
  review,
}: {
  review: ReturnType<typeof usePeriodReview>;
}) => {
  const data = review.state.data;
  if (data?.review)
    return (
      <Badge variant="secondary" color="success">
        <CheckIcon aria-hidden data-icon="inline-start" />
        Reviewed
      </Badge>
    );
  if (data?.revised_after_review)
    return (
      <Badge variant="secondary" color="warning">
        Revised after review
      </Badge>
    );
  return null;
};

export const MarkReviewedButton = ({
  review,
  label = "Mark reviewed",
  size = "default",
  asMenuItem = false,
  variant = "default",
}: {
  review: ReturnType<typeof usePeriodReview>;
  label?: string;
  size?: "sm" | "default";
  asMenuItem?: boolean;
  variant?: "default" | "ghost";
}) => {
  const data = review.state.data;
  // Signing a period off is finance's; a manager it was shared with sees
  // the Reviewed badge instead.
  const canManage = useCanManageProfitability();
  if (!canManage) return null;
  if (review.state.isLoading)
    return asMenuItem ? (
      <DropdownMenuItem disabled>Loading review…</DropdownMenuItem>
    ) : (
      <Skeleton className="h-9 w-32" aria-hidden />
    );
  if (!data) return null;
  if (asMenuItem)
    return (
      <DropdownMenuItem
        disabled={review.mark.isPending}
        onClick={() => review.mark.mutate(!data.review)}
      >
        {data.review ? "Undo review" : label}
      </DropdownMenuItem>
    );
  if (data.review)
    return (
      <Button
        variant="ghost"
        size={size}
        disabled={review.mark.isPending}
        onClick={() => review.mark.mutate(false)}
        title={`Reviewed${data.review.reviewed_by ? ` by ${data.review.reviewed_by.full_name}` : ""} ${dayjs(data.review.reviewed_at).format("MMM D")}`}
      >
        Undo review
      </Button>
    );
  return (
    <Button
      color="neutral"
      variant={variant}
      size={size}
      loading={review.mark.isPending}
      onClick={() => review.mark.mutate(true)}
    >
      {label}
    </Button>
  );
};
